// components/EditableCodeArea.tsx
'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { vue } from '@codemirror/lang-vue';
import { oneDark } from '@codemirror/theme-one-dark';
import { Copy, Download, Save, RotateCw, Undo2, Redo2 } from 'lucide-react';
import copy from 'clipboard-copy';
export type Framework = 'html' | 'react' | 'vue' | 'uniapp';

interface EditableCodeAreaProps {
  code: string;
  framework: Framework;
  fileName?: string;
  onCodeChange: (newCode: string, framework: Framework) => Promise<void>;
  isConverting?: boolean;
  readOnly?: boolean;
}

export default function EditableCodeArea({
  code,
  framework,
  fileName = 'code',
  onCodeChange,
  isConverting = false,
  readOnly = false
}: EditableCodeAreaProps) {
  const [editedCode, setEditedCode] = useState(code);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editHistory, setEditHistory] = useState<string[]>([code]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const editorRef = useRef<any>(null);

  // 当外部代码更新时，同步到编辑器
  useEffect(() => {
    if (!isEditing) {
      setEditedCode(code);
      setEditHistory([code]);
      setHistoryIndex(0);
    }
  }, [code, isEditing]);

  // 获取编辑器语言配置
  const getExtensions = useCallback(() => {
    switch (framework) {
      case 'html':
        return [html()];
      case 'react':
        return [javascript({ jsx: true })];
      case 'vue':
      case 'uniapp':
        return [vue()];
      default:
        return [javascript()];
    }
  }, [framework]);

  // 获取文件扩展名
  const getFileExtension = () => {
    switch (framework) {
      case 'html': return '.html';
      case 'react': return '.jsx';
      case 'vue': return '.vue';
      case 'uniapp': return '.vue';
      default: return '.txt';
    }
  };

  // 处理代码变更
  const handleChange = useCallback((value: string) => {
    setEditedCode(value);
    setIsEditing(true);
    
    // 记录编辑历史（最多50条）
    if (editHistory[historyIndex] !== value) {
      const newHistory = editHistory.slice(0, historyIndex + 1);
      newHistory.push(value);
      if (newHistory.length > 50) {
        newHistory.shift();
      }
      setEditHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [editHistory, historyIndex]);

  // 保存编辑
  const handleSave = async () => {
    if (editedCode === code) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onCodeChange(editedCode, framework);
      setIsEditing(false);
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 2000);
    } catch (error) {
      console.error('保存失败:', error);
      alert('保存失败，请重试');
    } finally {
      setIsSaving(false);
    }
  };

  // 撤销
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setEditedCode(editHistory[newIndex]);
    }
  };

  // 重做
  const handleRedo = () => {
    if (historyIndex < editHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setEditedCode(editHistory[newIndex]);
    }
  };

  // 复制代码

const copyToClipboard = async () => {
  try {
    await copy(editedCode);
    alert('代码已复制到剪贴板！');
  } catch (err) {
    console.error('复制失败:', err);
  }
};
  // 下载代码
  const downloadCode = () => {
    const blob = new Blob([editedCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}${getFileExtension()}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 放弃编辑
  const handleCancel = () => {
    setEditedCode(code);
    setIsEditing(false);
    setEditHistory([code]);
    setHistoryIndex(0);
  };

  // 格式化代码
  const formatCode = () => {
    try {
      let formatted = editedCode;
      
      // 简单的格式化逻辑
      if (framework === 'html') {
        formatted = formatted
          .replace(/>\s+</g, '>\n<')
          .replace(/\s+$/gm, '')
          .trim();
      } else if (framework === 'react' || framework === 'vue' || framework === 'uniapp') {
        // 添加基本的缩进格式化
        formatted = formatted
          .replace(/{/g, '{\n  ')
          .replace(/}/g, '\n}')
          .replace(/,\s*/g, ',\n  ');
      }
      
      setEditedCode(formatted);
    } catch (error) {
      console.error('格式化失败:', error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* 编辑器头部 */}
      <div className="flex items-center justify-between bg-gray-800 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
          </div>
          <div className="text-gray-300 font-medium text-sm">
            {fileName}
            <span className="text-gray-500 ml-2">{getFileExtension()}</span>
            {isConverting && (
              <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs rounded flex items-center gap-1">
                <RotateCw className="w-3 h-3 animate-spin" />
                同步中
              </span>
            )}
            {showSaveSuccess && (
              <span className="ml-2 px-2 py-0.5 bg-green-500/20 text-green-300 text-xs rounded">
                ✓ 已保存
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 编辑控制按钮 */}
          {readOnly ? (
            <span className="px-3 py-1.5 bg-gray-700 text-gray-300 text-sm rounded-lg">
              只读模式
            </span>
          ) : (
            <>
              {isEditing && (
                <>
                  <button
                    onClick={handleUndo}
                    disabled={historyIndex <= 0}
                    className="p-1.5 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50"
                    title="撤销"
                  >
                    <Undo2 size={16} />
                  </button>
                  <button
                    onClick={handleRedo}
                    disabled={historyIndex >= editHistory.length - 1}
                    className="p-1.5 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50"
                    title="重做"
                  >
                    <Redo2 size={16} />
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || editedCode === code}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    <Save className="w-4 h-4" />
                    {isSaving ? '保存中...' : '保存'}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-3 py-1.5 bg-gray-700 text-gray-300 text-sm rounded-lg hover:bg-gray-600"
                  >
                    取消
                  </button>
                </>
              )}
            </>
          )}

          {/* 通用操作按钮 */}
          <button
            onClick={formatCode}
            className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
            title="格式化代码"
          >
            ⌘F
          </button>
          <button
            onClick={copyToClipboard}
            className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
            title="复制代码"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={downloadCode}
            className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            title="下载代码"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 代码编辑器区域 */}
        <div className="h-full overflow-y-auto">
          <div className="flex-1 overflow-hidden">
            <CodeMirror
              ref={editorRef}
              value={editedCode}
              height="100%"
              theme={oneDark}
              extensions={getExtensions()}
              onChange={handleChange}
              readOnly={readOnly}
              basicSetup={{
                lineNumbers: true,
                highlightActiveLineGutter: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: true,
                rectangularSelection: true,
                crosshairCursor: true,
                highlightActiveLine: true,
                foldGutter: true,
                dropCursor: true,
                allowMultipleSelections: true,
                indentOnInput: true,
                syntaxHighlighting: true,
                tabSize: 2,
              }}
            />
          </div>
        </div>
      {/* 编辑器底部状态栏 */}
      <div className="bg-gray-800 border-t border-gray-700 px-4 py-2">
        <div className="flex justify-between text-xs text-gray-400">
          <div className="flex items-center gap-4">
            <span>框架: {framework.toUpperCase()}</span>
            <span>长度: {editedCode.length} 字符</span>
            <span>行数: {editedCode.split('\n').length}</span>
            <span>历史: {editHistory.length} 条</span>
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <span className="text-yellow-400">● 未保存的更改</span>
            ) : readOnly ? (
              <span className="text-gray-400">🔒 只读模式</span>
            ) : (
              <span className="text-green-400">✓ 已保存</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}